import {OktoberfestGameAssets} from "../OktoberfestGameAssets";
import {Oktoberfest} from "../Oktoberfest";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {TweenLite, Linear} from "gsap";
import {GameHelper} from "../../core/utils/GameHelper";
import {OktoberfestGameConfig} from "../OktoberfestGameConfig";
import {IWinRatioConfig} from "../../core/config/WinRatio";
import {Layers, OktoberfestLayers} from "./OktoberfestLayers";
import {OktoberfestSound} from "../sound/OktoberfestSound";

export class GameBackground extends PIXI.Container {

    private OVERLAY_BG: string = "win-lightup_bg";
    private OVERLAY_FG: string = "win-lightup_fg";
    private TOP_OVERLAY_GRADIENT: string = "topOverlay";
    private BACKGROUND_BLUR_AMOUNT: number = 7;
    private _mainGameBg: PIXI.Sprite;
    private _mainGameBlurBg: PIXI.Sprite;
    private _portraitOverlays: PIXI.Container;
    private _winBgOverlay: PIXI.Container;
    private _winFgOverlay: PIXI.Container;
    private _backgroundContainer: PIXI.Container;

    private _freespinIntroBackgroundTween: TweenLite;
    private _freespinIntroPanningTween: TweenLite;
    private _freespinIntroBackgroundOffsetX: number;

    constructor() {
        super();
        this.init();
        this.onResize();
        OktoberfestLayers.getLayer(Layers.BACKGROUND)!.addChild(this);
    }

    public onResize(): void {
        if (NolimitApplication.isLandscape) {
            this.onLandscape();
        } else {
            this.onPortrait(NolimitApplication.screenBounds);
        }
        this._mainGameBlurBg.position.set(this._mainGameBg.position.x, this._mainGameBg.position.y);
    }
    public onFreespinIntroPanning(duration: number): void {
        if (GameHelper.isDesktop()) {
            this._mainGameBlurBg.alpha = 1;
            this._freespinIntroBackgroundTween = new TweenLite(this._mainGameBlurBg, {
                alpha: 0,
                duration: OktoberfestGameConfig.TIMING_FREESPIN_INTRO_BLUR_DURATION,
                ease: Linear.easeNone
            });

            this._freespinIntroPanningTween = new TweenLite([this._mainGameBlurBg,this._mainGameBg], {
                x: this._freespinIntroBackgroundOffsetX,
                duration: duration,
                ease: Linear.easeNone
            });
            OktoberfestSound.soundPlayer.playTrack({name:OktoberfestSound.SOUND_NAME_FS_PANNING_SCREEN});
        }
    }

    public onFreespinIntroViewClosed(): void {
        if (GameHelper.isDesktop()) {
            if (this._freespinIntroPanningTween && this._freespinIntroPanningTween.isActive()) {
                this._freespinIntroPanningTween.kill();
            }
            if (this._freespinIntroBackgroundTween && this._freespinIntroBackgroundTween.isActive()) {
                this._freespinIntroBackgroundTween.kill();
            }

            this._mainGameBlurBg.alpha = 1;
            this._mainGameBg.x = this._freespinIntroBackgroundOffsetX;
            this._mainGameBlurBg.x = this._freespinIntroBackgroundOffsetX;
            this._mainGameBlurBg.visible = true;
        }
    }

    public onFreespinOutroViewShow(): void {
        if (GameHelper.isDesktop()) {
            this.fadeWinOverlay(0.5);
        }
    }

    public onFreespinOutroViewClosed(): void {
        if (GameHelper.isDesktop()) {
            this._mainGameBg.x = 304;
            this._mainGameBlurBg.x = 304;
        }
    }

    public onSpinStart(): void {
        this.fadeWinOverlay(OktoberfestGameConfig.TIMING_FREESPIN_INTRO_PANNING_DURATION);
    }

    public onInitialWinPresentationStated(currentWinRatio: number): void {
        const winRatios: IWinRatioConfig[] = OktoberfestGameConfig.WIN_RATIOS;
        //Big
        let opacity: number = 1;

        //Large
        if (currentWinRatio < winRatios[3].ratio) {
            opacity = 0.75;
        }

        //Medium
        if (currentWinRatio <= winRatios[2].ratio) {
            opacity = 0.45;
        }

        //small
        if (currentWinRatio <= winRatios[1].ratio) {
            opacity = 0.15;
        }

        TweenLite.to([this._winBgOverlay, this._winFgOverlay], {duration: 3, alpha: opacity});
    }

    protected onLandscape(): void {
        this._mainGameBg.position.set(356, 140);
        this._portraitOverlays.visible = false;
        this._winBgOverlay.scale.set(1);

        if (this._freespinIntroPanningTween && this._freespinIntroPanningTween.isActive()) {
            this._freespinIntroPanningTween.kill();
        }
        this._winBgOverlay.visible = true;
        this._winBgOverlay.getChildAt(0).scale.set(2.05);
        this._winFgOverlay.getChildAt(0).scale.set(2.05);
        if(NolimitApplication.screenBounds.width/NolimitApplication.screenBounds.height>1.77){
            this._winBgOverlay.getChildAt(0).scale.set(2.5,2.05);
            this._winFgOverlay.getChildAt(0).scale.set(2.7,2.2);
            this._winBgOverlay.getChildAt(0).position.set(413,360);
            this._winFgOverlay.getChildAt(0).position.set(413,360);
        }
    }

    protected onPortrait(data:any): void {
        this._mainGameBg.position.set(356, 147);
        this._portraitOverlays.position.set(360, -( ( NolimitApplication.screenBounds.height * 0.5 ) - ( this._portraitOverlays.height * 0.5 ) - 360 ));
        this._portraitOverlays.visible = true;
        this._winBgOverlay.visible = false;
        this._winBgOverlay.getChildAt(0).scale.set(1.3, 4);
        this._winFgOverlay.getChildAt(0).scale.set(1.3, 4);
    }

    private createOverlay(): PIXI.Sprite {
        const overlay: PIXI.Sprite = new PIXI.Sprite(Oktoberfest.assetLoader.getTextures(this.TOP_OVERLAY_GRADIENT)[0]);
        overlay.anchor.set(0.5);
        overlay.scale.set(1800, 1);

        return overlay;
    }

    private init(): void {
        this._backgroundContainer = new PIXI.Container();
        this._freespinIntroBackgroundOffsetX = 304;

        this.createMainBackground();
        this.createBlurredBackground();
        this.createPortraitTopOverlay();
        this.createWinOverlays();
    }

    private createMainBackground(): void {
        this._mainGameBg = new PIXI.Sprite(Oktoberfest.assetLoader.getTextures(OktoberfestGameAssets.MAIN_GAME_BACKGROUND)[0]);
        this._mainGameBg.anchor.set(0.5);
        this._mainGameBg.name = "MAIN_BG";
        this._backgroundContainer.addChild(this._mainGameBg);
        this.addChild(this._backgroundContainer);
    }

    private createBlurredBackground(): void {
        this._mainGameBlurBg = new PIXI.Sprite(Oktoberfest.assetLoader.getTextures(OktoberfestGameAssets.MAIN_GAME_BACKGROUND)[0]);
        this._mainGameBlurBg.anchor.set(0.5);
        this._mainGameBlurBg.scale.set(this._mainGameBg.scale.x, this._mainGameBg.scale.y);
        this._mainGameBlurBg.name = "BLURRED_BG";

        const blur: PIXI.filters.BlurFilter = new PIXI.filters.BlurFilter();
        blur.blurX = this.BACKGROUND_BLUR_AMOUNT;
        blur.blurY = this.BACKGROUND_BLUR_AMOUNT;
        this._mainGameBlurBg.filters = [blur];
        this._backgroundContainer.addChild(this._mainGameBlurBg);
    }

    private createPortraitTopOverlay(): void {
        this._portraitOverlays = new PIXI.Container();
        this._portraitOverlays.visible = false;
        this._portraitOverlays.name = "PORTRAIT_OVERLAY";

        const overlay_1: PIXI.Sprite = this.createOverlay(),
            overlay_2: PIXI.Sprite = this.createOverlay();

        this._portraitOverlays.addChild(overlay_1, overlay_2);
        this._backgroundContainer.addChild(this._portraitOverlays);
    }

    private createWinOverlays(): void {
        const winBgEffect: PIXI.Sprite = new PIXI.Sprite(Oktoberfest.assetLoader.getTextures(this.OVERLAY_BG)[0]),
            winFgEffect: PIXI.Sprite = new PIXI.Sprite(Oktoberfest.assetLoader.getTextures(this.OVERLAY_FG)[0]);

        winBgEffect.blendMode = PIXI.BLEND_MODES.SCREEN;
        winBgEffect.alpha = 1;
        winBgEffect.anchor.set(0.5);
        winBgEffect.scale.set(2.05);
        winBgEffect.position.set(360);

        winFgEffect.blendMode = PIXI.BLEND_MODES.SCREEN;
        winFgEffect.alpha = 1;
        winFgEffect.anchor.set(0.5);
        winFgEffect.scale.set(2.05);
        winFgEffect.position.set(360);

        this._winBgOverlay = new PIXI.Container();
        this._winBgOverlay.name = "WIN_BG_OVERLAY";
        this._winBgOverlay.alpha = 0;
        this._winBgOverlay.addChild(winBgEffect);

        this._winFgOverlay = new PIXI.Container();
        this._winFgOverlay.name = "WIN_FG_OVERLAY";
        this._winFgOverlay.alpha = 0;
        this._winFgOverlay.addChild(winFgEffect);
        OktoberfestLayers.getLayer(Layers.FOREGROUND)!.addChild(this._winBgOverlay, this._winFgOverlay);
    }

    public fadeWinOverlay(dur: number): void {
        TweenLite.to([this._winBgOverlay, this._winFgOverlay], {duration: dur, alpha: 0});
    }
}
